import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'lib/utils';
import { Avatar as AvatarPrimitive } from 'radix-ui';
import * as React from 'react';

const avatarVariants = cva('relative flex shrink-0 overflow-hidden rounded-full', {
  variants: {
    size: {
      sm: 'size-6',
      default: 'size-8',
      lg: 'size-10',
      /** The homepage messaging card's avatars, alone or stacked. */
      card: 'size-12',
      /** The referring caregiver's portrait on the homepage card. */
      portrait: 'size-22',
      /** The profile dialogs' portrait, and the referring caregiver's on the messaging page. */
      xl: 'size-24',
    },
  },
  defaultVariants: { size: 'default' },
});

/** Figma: "Avatar" (+ "Avatar Stack", "Avatar With Status Badge"). */
function Avatar({
  className,
  size,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> & VariantProps<typeof avatarVariants>) {
  return <AvatarPrimitive.Root data-slot="avatar" className={cn(avatarVariants({ size }), className)} {...props} />;
}

function AvatarImage({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn('aspect-square size-full object-cover', className)}
      {...props}
    />
  );
}

function AvatarFallback({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        'bg-muted text-muted-foreground flex size-full items-center justify-center rounded-full text-sm',
        className,
      )}
      {...props}
    />
  );
}

/** Overlapping avatars, each ringed with the card colour. */
function AvatarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="avatar-group"
      className={cn('flex -space-x-2 *:data-[slot=avatar]:ring-card *:data-[slot=avatar]:ring-2', className)}
      {...props}
    />
  );
}

export { Avatar, AvatarImage, AvatarFallback, AvatarGroup };
